/** @prettier */
import { useEffect } from "react";
import { getImageThumbnails } from "../../Question/questionApi";
import { image } from "../../util/imageUtils";
import { QuestionData } from "../Run/testRunnerApi";

function getAtIndex<T>(arr: T[], index: number) {
    return arr[index] as T | undefined;
}

export type Image = image;

export type ImagesLoadState = {
    // Images will be loaded on demand
    images?:
        | { state: "loading" }
        | {
              state: "loaded";
              stemImages: Array<Image>;
              // Will always be [] in TestRunner
              explanationImages: Array<Image>;
          };
};

// Generic because the QuestionData varies between review and runner
type State<Q extends QuestionData & ImagesLoadState> =
    | { state: "loading" }
    | {
          state: "loaded";
          questions: Q[];
          currentQuestionIndex: number;
      };
export type ImageLoadActions =
    | { event: "loadingImages"; questionId: string }
    | {
          event: "loadedImages";
          questionId: string;
          stemImages: Image[];
          explanationImages: Image[];
      };

export const useQuestionImages = <Q extends QuestionData & ImagesLoadState>(
    state: State<Q>,
    dispatch: (event: ImageLoadActions) => void,
) => {
    // Image loading (current question and next question)
    useEffect(() => {
        if (state.state !== "loaded") return;
        const question = getAtIndex(
            state.questions,
            state.currentQuestionIndex,
        );
        const nextQuestion = getAtIndex(
            state.questions,
            state.currentQuestionIndex + 1,
        );
        function loadImagesForQuestion(questionId: string) {
            dispatch({ event: "loadingImages", questionId: questionId });
            getImageThumbnails(questionId).then(
                ([stemImages, explanationImages]) => {
                    dispatch({
                        event: "loadedImages",
                        questionId: questionId,
                        stemImages,
                        explanationImages,
                    });
                },
            );
        }
        // Load images for the current question if they're not already loaded
        if (question && !question.images) {
            loadImagesForQuestion(question.id);
        }
        // Load images for the next question if they're not already loaded.
        // This should help avoid image pop-in
        if (nextQuestion && !nextQuestion.images) {
            loadImagesForQuestion(nextQuestion.id);
        }
    }, [state, dispatch]);
};
